import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { detectPreferredLanguage } from "../utils/languageDetect";

/** Same stacks as quark-web `src/app/theme/fontFamilies.ts` */
const FONT_FAMILY_AR =
  '"GT America Arabic", Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const FONT_FAMILY_EN =
  'Inter, "GT America Arabic", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const LANGUAGE_STORAGE_KEY = "tabbaqa-language";

type Language = "en" | "ar";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  dir: "ltr" | "rtl";
  fontFamily: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // من localStorage أولاً — لو المستخدم زار من قبل أو اختار اللغة يدوياً
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
      if (saved === "ar" || saved === "en") return saved;
    }
    return "ar"; // افتراضي عربي — للـ SSR ومعاينة السوشيال
  });

  const hasCheckedPreference = useRef(false);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);

      // Force update HTML attributes immediately
      document.documentElement.setAttribute("lang", lang);
      document.documentElement.setAttribute(
        "dir",
        lang === "ar" ? "rtl" : "ltr"
      );
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";

      // Update body
      document.body.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");

      // Update font family
      updateFontFamily(lang);

      // Force reflow
      document.documentElement.style.display = "none";
      document.documentElement.offsetHeight;
      document.documentElement.style.display = "";
    }
  }, []);

  const updateFontFamily = (lang: Language) => {
    if (typeof document !== "undefined") {
      document.documentElement.style.fontFamily =
        lang === "ar" ? FONT_FAMILY_AR : FONT_FAMILY_EN;
    }
  };

  useEffect(() => {
    // Respect the browser language on the first visit. A saved manual choice
    // always takes priority.
    if (typeof window !== "undefined" && !hasCheckedPreference.current) {
      hasCheckedPreference.current = true;
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
      if (saved !== "ar" && saved !== "en") {
        setLanguage(detectPreferredLanguage());
      }
    }
  }, [setLanguage]);

  useEffect(() => {
    // Initialize on mount
    if (typeof window !== "undefined") {
      const savedLang =
        (localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language) || "ar";
      const currentLang = savedLang || language;

      // Force update HTML attributes
      document.documentElement.setAttribute("lang", currentLang);
      document.documentElement.setAttribute(
        "dir",
        currentLang === "ar" ? "rtl" : "ltr"
      );
      document.documentElement.lang = currentLang;
      document.documentElement.dir = currentLang === "ar" ? "rtl" : "ltr";

      updateFontFamily(currentLang);

      // Also update body if needed
      document.body.setAttribute("dir", currentLang === "ar" ? "rtl" : "ltr");
    }
  }, []);

  useEffect(() => {
    // Update when language changes
    if (typeof window !== "undefined") {
      // Force update HTML attributes
      document.documentElement.setAttribute("lang", language);
      document.documentElement.setAttribute(
        "dir",
        language === "ar" ? "rtl" : "ltr"
      );
      document.documentElement.lang = language;
      document.documentElement.dir = language === "ar" ? "rtl" : "ltr";

      updateFontFamily(language);

      // Also update body if needed
      document.body.setAttribute("dir", language === "ar" ? "rtl" : "ltr");

      // Force reflow to ensure changes take effect
      document.documentElement.style.display = "none";
      document.documentElement.offsetHeight; // Trigger reflow
      document.documentElement.style.display = "";
    }
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    dir: language === "ar" ? "rtl" : "ltr",
    fontFamily: language === "ar" ? FONT_FAMILY_AR : FONT_FAMILY_EN,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
